/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumb, Modal, Form, Select, Input, Button, Result } from 'antd';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/common/MainLayout';
import UsersSection from '../../components/common/UsersSection';
import { createInvite, resetInvite } from '../../store/slices/inviteSlice';
import { AppDispatch } from '../../store/store';

const styles = {
  copyLinkButton: css({ marginTop: 8 }),
  inviteInstructions: css({ marginTop: 8 }),
};

const SaveUsersPage: React.FC = () => {
  const { t } = useTranslation('saves');
  const { saveId } = useParams<{ saveId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [inviteForm] = Form.useForm();
  const inviteLink = useSelector((state: any) => state.invite.link) as string | null;
  const inviteLoading = useSelector((state: any) => state.invite.loading) as boolean;

  if (!saveId) {
    return (
      <MainLayout>
        <Result status="404" title={t('shared.saveNotFound')} />
      </MainLayout>
    );
  }

  const handleInviteOk = async () => {
    try {
      const values = await inviteForm.validateFields();
      await dispatch(createInvite({ saveId, roleCode: values.roleCode })).unwrap();
      inviteForm.resetFields();
    } catch (e: any) {
      if (!e?.errorFields) {
        // errors handled globally
      }
    }
  };

  const handleInviteCancel = () => {
    setInviteModalVisible(false);
    dispatch(resetInvite());
    inviteForm.resetFields();
  };

  const fullInviteLink = inviteLink ? `${window.location.protocol}//${window.location.host}/invite/${inviteLink}` : null;

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/saves">{t('shared.breadcrumbSaves')}</Link> },
            { title: <Link to={`/saves/${saveId}`}>{t('shared.breadcrumbSave')}</Link> },
            { title: t('users.breadcrumbAccess') },
          ]}
        />
      }
    >
      <UsersSection saveId={saveId} onInviteClick={() => setInviteModalVisible(true)} />
      <Modal
        title={t('users.inviteUser')}
        open={inviteModalVisible}
        onOk={() => {
          if (inviteLink) {
            handleInviteCancel();
          } else {
            handleInviteOk();
          }
        }}
        onCancel={handleInviteCancel}
        okText={inviteLink ? t('common:actions.close') : t('common:actions.create')}
        cancelText={inviteLink ? undefined : t('common:actions.cancel')}
        confirmLoading={inviteLoading}
      >
        {!inviteLink ? (
          <Form form={inviteForm} layout="vertical">
            <Form.Item label={t('users.columns.role')} name="roleCode" rules={[{ required: true, message: t('users.selectRole') }]}>
              <Select
                options={[
                  { value: 'manager', label: t('users.roles.manager') },
                  { value: 'worker', label: t('users.roles.worker') },
                  { value: 'guest', label: t('users.roles.guest') },
                ]}
              />
            </Form.Item>
          </Form>
        ) : (
          <div>
            <p>{t('users.inviteLink')}</p>
            <Input value={fullInviteLink || ''} readOnly />
            <Button
              css={styles.copyLinkButton}
              onClick={() => fullInviteLink && navigator.clipboard.writeText(fullInviteLink)}
            >
              {t('users.copyLink')}
            </Button>
            <p css={styles.inviteInstructions}>{t('users.sendLinkHint')}</p>
          </div>
        )}
      </Modal>
    </MainLayout>
  );
};

export default SaveUsersPage;